import React from 'react';
import { tooltipDate, type PlannedDatesMap } from '../utils/scheduleGanttMapping';

function formatPlannedAccomplishment(value: number | null | undefined): string {
  if (value == null || Number.isNaN(Number(value))) return '–';
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 6 });
}

/** Tooltip content: task name + planned start and completion. Uses plannedDatesMap so we show API dates even when Gantt doesn't pass custom fields. */
export function ScheduleTaskTooltipContent({
  data,
  plannedDatesMap,
  assigneesByGanttId,
}: {
  data?: { id?: number | string; text?: string; start?: Date | string; end?: Date | string; progress?: number };
  api?: unknown;
  plannedDatesMap?: PlannedDatesMap;
  /** Task Schedule: employee names for this Gantt row (key = synthetic row id). */
  assigneesByGanttId?: Map<number | string, string>;
}) {
  if (!data) return null;
  const id = data.id != null ? data.id : undefined;
  const assigneesLine =
    id !== undefined && assigneesByGanttId
      ? assigneesByGanttId.get(id) ??
        assigneesByGanttId.get(Number(id)) ??
        assigneesByGanttId.get(String(id))
      : undefined;
  const planned =
    id !== undefined && plannedDatesMap
      ? plannedDatesMap.get(id) ?? plannedDatesMap.get(Number(id)) ?? plannedDatesMap.get(String(id))
      : undefined;
  const startStr =
    planned?.plannedStartDate != null && planned.plannedStartDate !== ''
      ? tooltipDate(planned.plannedStartDate)
      : planned
        ? '–'
        : tooltipDate(data.start);
  const endStr =
    planned?.plannedCompletionDate != null && planned.plannedCompletionDate !== ''
      ? tooltipDate(planned.plannedCompletionDate)
      : planned
        ? '–'
        : tooltipDate(data.end);
  const yearRoundNote = planned?.yearRoundSchedule === true;
  const plannedAccomplishment =
    typeof planned?.identifiedAccomplishment === 'number' && Number.isFinite(planned.identifiedAccomplishment)
      ? planned.identifiedAccomplishment
      : null;
  return (
    <div className="schedule-task-tooltip">
      {data.text && <div className="schedule-task-tooltip-title">{data.text}</div>}
      {yearRoundNote ? (
        <div className="schedule-task-tooltip-row tw-text-content-muted tw-text-sm">
          Year-round / as-needed work (bar spans the visible schedule range when dates are not set).
        </div>
      ) : null}
      <div className="schedule-task-tooltip-row">
        <span className="schedule-task-tooltip-label">Planned start:</span> {startStr}
      </div>
      <div className="schedule-task-tooltip-row">
        <span className="schedule-task-tooltip-label">Planned completion:</span> {endStr}
      </div>
      <div className="schedule-task-tooltip-row">
        <span className="schedule-task-tooltip-label">Planned accomplishment:</span>{' '}
        {formatPlannedAccomplishment(plannedAccomplishment)}
      </div>
      {assigneesLine ? (
        <div className="schedule-task-tooltip-row">
          <span className="schedule-task-tooltip-label">Assigned:</span> {assigneesLine}
        </div>
      ) : null}
      {planned?.dependencies && Array.isArray(planned.dependencies.triggers) && planned.dependencies.triggers.length > 0 ? (
        <>
          <div className="schedule-task-tooltip-row">
            <span className="schedule-task-tooltip-label">Dependencies:</span> {planned.dependencies.triggers.join(', ')}
          </div>
          {planned.dependencies.resourceDependentType ? (
            <div className="schedule-task-tooltip-row">
              <span className="schedule-task-tooltip-label">Resource dependent:</span> {planned.dependencies.resourceDependentType}
            </div>
          ) : null}
          {planned.dependencies.completionTaskId ? (
            <div className="schedule-task-tooltip-row">
              <span className="schedule-task-tooltip-label">Completion of task:</span> #{planned.dependencies.completionTaskId}
            </div>
          ) : null}
          {planned.dependencies.otherComment ? (
            <div className="schedule-task-tooltip-row">
              <span className="schedule-task-tooltip-label">Other:</span> {planned.dependencies.otherComment}
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
